import { useState, useEffect } from 'react';
import { backdropClose } from '../utils/backdropClose';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import { useUserContext } from '../contexts/UserContext';

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (memberId?: string) => void;
}

interface FormData {
  name: string;
  displayName: string;
  email: string;
  phoneNo: string;
  dateOfBirth: string;
  gender: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  address: string;
  city: string;
  pincode: string;
  notes: string;
  gymId: string;
  branchId: string;
  memberStatus: 'lead' | 'trial';
  trialDays: string;
  termsAccepted: boolean;
  whatsappOptIn: boolean;
}

interface BranchOption {
  id: string;
  name: string;
  displayName: string | null;
}

export default function AddMemberModal({ isOpen, onClose, onSuccess }: AddMemberModalProps) {
  const { userContext } = useUserContext();
  const [loading, setLoading] = useState(false);

  // Get gymId from user context
  const currentGymId = userContext?.accessibleGyms?.[0] || '';

  const [formData, setFormData] = useState<FormData>({
    name: '',
    displayName: '',
    email: '',
    phoneNo: '',
    dateOfBirth: '',
    gender: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    address: '',
    city: '',
    pincode: '',
    notes: '',
    gymId: currentGymId,
    branchId: '',
    memberStatus: 'lead',
    trialDays: '3',
    termsAccepted: false,
    whatsappOptIn: false
  });
  const [formErrors, setFormErrors] = useState<Partial<FormData>>({});
  const [isWhatsappVerified, setIsWhatsappVerified] = useState(false);
  // Per-category notification subscription (ON = subscribed). New members are
  // subscribed to everything by default; turning one off opts them out.
  const [notifPrefs, setNotifPrefs] = useState<{ transactional: boolean; marketing: boolean }>({ transactional: true, marketing: true });
  const [branches, setBranches] = useState<BranchOption[]>([]);

  const currentRole = userContext?.roles.find(r => r.gymId === formData.gymId);
  const notificationsEnabled = currentRole?.plan?.features?.notificationsEnabled ?? false;
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  // Update gymId when modal opens
  useEffect(() => {
    if (isOpen && currentGymId) {
      setFormData(prev => ({ ...prev, gymId: currentGymId }));
    }
  }, [isOpen, currentGymId]);

  // Load branches for the Home Branch selector when the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch(`${API_BASE_URL}/api/branches`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const list: BranchOption[] = (data.data || data.branches || []).filter(
            (b: any) => (b.status ?? 'active') !== 'inactive'
          );
          setBranches(list);
          // Default to the single branch when there's only one.
          if (list.length === 1) {
            setFormData(prev => ({ ...prev, branchId: prev.branchId || list[0].id }));
          }
        }
      } catch {
        /* non-fatal: branch is optional */
      }
    })();
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'phoneNo') {
      setIsWhatsappVerified(false);
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (formErrors[name as keyof FormData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<FormData> = {};
    const nameRegex = /^[A-Za-z\s]+$/;

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (!nameRegex.test(formData.name.trim())) {
      errors.name = 'Name can only contain letters and spaces';
    }

    if (formData.displayName && formData.displayName.trim()) {
      if (!nameRegex.test(formData.displayName.trim())) {
        errors.displayName = 'Display name can only contain letters and spaces';
      }
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!formData.phoneNo.trim()) {
      errors.phoneNo = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phoneNo.replace(/\D/g, ''))) {
      errors.phoneNo = 'Please enter a valid 10-digit Indian phone number';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (formData.dateOfBirth) {
      const dob = new Date(formData.dateOfBirth);
      const today = new Date();
      if (dob > today) {
        errors.dateOfBirth = 'Date of birth cannot be a future date';
      } else {
        const age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (age < 14 || (age === 14 && monthDiff < 0)) {
          errors.dateOfBirth = 'Member must be at least 14 years old';
        }
      }
    }

    // Emergency contact validation - optional but must be valid if provided
    if (formData.emergencyContactName && formData.emergencyContactName.trim()) {
      if (formData.emergencyContactName.trim().length < 2) {
        errors.emergencyContactName = 'Name must be at least 2 characters';
      }
      if (!nameRegex.test(formData.emergencyContactName.trim())) {
        errors.emergencyContactName = 'Name can only contain letters and spaces';
      }
    }

    if (formData.emergencyContactPhone && formData.emergencyContactPhone.trim()) {
      const emergencyPhoneRegex = /^[6-9]\d{9}$/;
      if (!emergencyPhoneRegex.test(formData.emergencyContactPhone.replace(/\D/g, ''))) {
        errors.emergencyContactPhone = 'Please enter a valid 10-digit Indian phone number';
      }
    }

    if (formData.pincode && formData.pincode.trim()) {
      const pincodeRegex = /^[1-9][0-9]{5}$/;
      if (!pincodeRegex.test(formData.pincode.trim())) {
        errors.pincode = 'Please enter a valid 6-digit pincode';
      }
    }

    if (formData.address && formData.address.trim()) {
      if (formData.address.trim().length < 5) {
        errors.address = 'Address must be at least 5 characters';
      }
    }

    if (!formData.gymId) {
      errors.gymId = 'Gym ID is required';
    }

    if (!formData.termsAccepted) {
      errors.termsAccepted = 'Member must accept Terms & Conditions' as any;
    }

    if (formData.memberStatus === 'trial') {
      const trialDaysNum = parseInt(formData.trialDays);
      if (isNaN(trialDaysNum) || trialDaysNum < 1 || trialDaysNum > 5) {
        errors.trialDays = 'Trial duration must be between 1 and 5 days';
      }
    }

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      toast.error(firstError as string);
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSendOtp = async () => {
    if (!formData.phoneNo || formData.phoneNo.length < 10) {
      toast.error('Please enter a valid phone number first');
      return;
    }

    setIsSendingOtp(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${API_BASE_URL}/api/members/whatsapp/send-otp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phoneNumber: formData.phoneNo,
          gymId: formData.gymId
        })
      });

      if (response.ok) {
        setShowOtpInput(true);
        toast.success('OTP sent to WhatsApp');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Error sending OTP');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter a 6-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${API_BASE_URL}/api/members/whatsapp/verify-otp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phoneNumber: formData.phoneNo,
          gymId: formData.gymId,
          otp
        })
      });

      if (response.ok) {
        setIsWhatsappVerified(true);
        setShowOtpInput(false);
        toast.success('WhatsApp verified successfully');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Invalid OTP');
      }
    } catch (error) {
      toast.error('Error verifying OTP');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;

      const response = await fetch(`${apiUrl}/api/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...formData, notificationPrefs: notifPrefs })
      });

      if (response.ok) {
        const responseData = await response.json();
        const memberId = responseData.data?.id;

        toast.success(
          formData.memberStatus === 'lead'
            ? 'Lead created successfully! Add membership from the member details.'
            : 'Trial member created successfully!'
        );

        onSuccess(memberId);
        onClose();

        // Reset form
        setFormData({
          name: '',
          displayName: '',
          email: '',
          phoneNo: '',
          dateOfBirth: '',
          gender: '',
          emergencyContactName: '',
          emergencyContactPhone: '',
          address: '',
          city: '',
          pincode: '',
          notes: '',
          gymId: currentGymId,
          branchId: '',
          memberStatus: 'lead',
          trialDays: '3',
          termsAccepted: false,
          whatsappOptIn: false
        });
      } else {
        const errorData = await response.json();

        if (errorData.suggestRestore && errorData.memberId) {
          toast((t) => (
            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">{errorData.error}</p>
              <button
                onClick={async () => {
                  toast.dismiss(t.id);
                  await handleRestore(errorData.memberId);
                }}
                className="px-3 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors"
              >
                Restore Member Now
              </button>
            </div>
          ), { duration: 6000, icon: '💡' });
        } else {
          toast.error(errorData.error || 'Failed to add member');
        }
      }
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error('Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (memberId: string) => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${API_BASE_URL}/api/members/${memberId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Member restored successfully');
        onSuccess(memberId);
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to restore member');
      }
    } catch (error) {
      toast.error('Error restoring member');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-2 sm:p-4" {...backdropClose(onClose)}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white p-4 sm:p-6 border-b border-gray-200 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Add New Member</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
          {/* Member Type Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Member Type
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className={`relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.memberStatus === 'lead'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
                }`}>
                <input
                  type="radio"
                  name="memberStatus"
                  value="lead"
                  checked={formData.memberStatus === 'lead'}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <div className="flex items-center space-x-2 mb-2">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.memberStatus === 'lead' ? 'border-blue-500' : 'border-gray-300'
                    }`}>
                    {formData.memberStatus === 'lead' && (
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    )}
                  </div>
                  <span className="font-semibold text-gray-900">Lead</span>
                </div>
                <p className="text-sm text-gray-600 ml-7">
                  Prospect for follow-up. Convert to membership later.
                </p>
              </label>

              <label className={`relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.memberStatus === 'trial'
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
                }`}>
                <input
                  type="radio"
                  name="memberStatus"
                  value="trial"
                  checked={formData.memberStatus === 'trial'}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <div className="flex items-center space-x-2 mb-2">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.memberStatus === 'trial' ? 'border-green-500' : 'border-gray-300'
                    }`}>
                    {formData.memberStatus === 'trial' && (
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    )}
                  </div>
                  <span className="font-semibold text-gray-900">Trial</span>
                </div>
                <p className="text-sm text-gray-600 ml-7">
                  Give short-term access. Convert to paid membership later.
                </p>
              </label>
            </div>

            {formData.memberStatus === 'trial' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 animate-in fade-in slide-in-from-top-2">
                <label className="block text-sm font-medium text-green-800 mb-2">
                  Trial Duration (Days: 1-5)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="number"
                    name="trialDays"
                    value={formData.trialDays}
                    onChange={handleInputChange}
                    min="1"
                    max="5"
                    className="w-24 px-3 py-2 border border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                  />
                  <span className={`text-sm ${parseInt(formData.trialDays) > 5 || parseInt(formData.trialDays) < 1 ? 'text-red-500 font-medium' : 'text-green-700'}`}>
                    {(parseInt(formData.trialDays) > 5 || parseInt(formData.trialDays) < 1)
                      ? 'Error: Duration must be between 1 and 5 days.'
                      : `The trial will automatically expire after ${formData.trialDays || '0'} days.`}
                  </span>
                </div>
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                💡 <strong>Tip:</strong> To add a membership plan, save the member first, then use the "Membership" tab in member details.
              </p>
            </div>
          </div>

          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Personal Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter full name"
                />
                {formErrors.name && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.displayName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter display name (optional)"
                />
                {formErrors.displayName && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.displayName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phoneNo"
                  value={formData.phoneNo}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.phoneNo ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter phone number"
                />
                {formErrors.phoneNo && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.phoneNo}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter email address (optional)"
                />
                {formErrors.email && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  Date of Birth
                  <span className="group relative">
                    <svg className="w-4 h-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 p-2 bg-gray-800 text-white text-[10px] rounded shadow-lg z-20">
                      Anyone below 14 years must register parent details and must be under parent's supervision.
                    </div>
                  </span>
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  max={new Date().toISOString().split('T')[0]} // Prevent future dates
                  min={new Date(new Date().setFullYear(new Date().getFullYear() - 100)).toISOString().split('T')[0]} // Reasonable min (100 years ago)
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.dateOfBirth ? 'border-red-500' : 'border-gray-300'}`}
                />
                {formErrors.dateOfBirth && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.dateOfBirth}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Emergency Contact
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Emergency Contact Name
                </label>
                <input
                  type="text"
                  name="emergencyContactName"
                  value={formData.emergencyContactName}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.emergencyContactName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter emergency contact name"
                />
                {formErrors.emergencyContactName && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.emergencyContactName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Emergency Contact Phone
                </label>
                <input
                  type="tel"
                  name="emergencyContactPhone"
                  value={formData.emergencyContactPhone}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.emergencyContactPhone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter emergency contact phone"
                />
                {formErrors.emergencyContactPhone && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.emergencyContactPhone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Address
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter full address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter city"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pincode
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.pincode ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter pincode"
                  />
                  {formErrors.pincode && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.pincode}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Home Branch */}
          {branches.length > 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                Branch
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Home Branch
                </label>
                <select
                  name="branchId"
                  value={formData.branchId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No home branch</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.displayName || b.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Used to manage and filter this member by branch. Check-in access is still
                  set by the member's membership plan.
                </p>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Additional Notes
            </h3>

            <div>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add any additional notes about the member"
              />
            </div>
          </div>

          {/* Consents - NEW SECTION */}
          <div className={`space-y-4 bg-gray-50 p-4 rounded-lg border-2 ${formErrors.termsAccepted ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
            <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
              Consents & Permissions
            </h3>

            <div className="space-y-3">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={(formData as any).termsAccepted}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }));
                    if (e.target.checked && formErrors.termsAccepted) {
                      setFormErrors(prev => ({ ...prev, termsAccepted: undefined }));
                    }
                  }}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Member accepts the Gym's <a href="/terms" target="_blank" className="text-blue-600 hover:underline">Terms & Conditions</a> and <a href="/privacy-policy" target="_blank" className="text-blue-600 hover:underline">Privacy Policy</a>. <span className="text-red-500">*</span>
                </span>
              </label>
              {formErrors.termsAccepted && (
                <p className="text-red-600 text-sm ml-6 font-medium">⚠️ Member must accept Terms & Conditions to proceed</p>
              )}

              <div className="text-sm text-gray-700">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center flex-wrap gap-2">
                    <span className={`font-medium ${notificationsEnabled ? 'text-gray-900' : 'text-gray-400'}`}>WhatsApp Notifications</span>
                    {!notificationsEnabled && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded-full whitespace-nowrap">Notifications Disabled</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isWhatsappVerified && (
                      <span className="flex items-center gap-1 text-[11px] font-semibold text-green-600">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Verified
                      </span>
                    )}
                    <button type="button" onClick={handleSendOtp} disabled={isSendingOtp || !formData.phoneNo || !notificationsEnabled}
                      className="text-[11px] font-bold text-blue-600 hover:text-blue-700 disabled:opacity-50">
                      {isSendingOtp ? 'Sending…' : (isWhatsappVerified ? 'Re-verify' : 'Verify')}
                    </button>
                  </div>
                </div>
                <p className="text-xs mt-1 text-gray-500">
                  Members receive messages by default. Turn a category off to opt this member out. Verifying on WhatsApp is optional.
                </p>

                {/* Category toggles — ON = subscribed, OFF = opted out */}
                <div className="mt-3 space-y-2">
                  <label className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-100 cursor-pointer">
                    <span>
                      <span className="block font-medium text-gray-800">Transactional</span>
                      <span className="block text-[11px] text-gray-400">Receipts, reminders, bookings</span>
                    </span>
                    <input type="checkbox" checked={notifPrefs.transactional}
                      onChange={(e) => setNotifPrefs(prev => ({ ...prev, transactional: e.target.checked }))}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" />
                  </label>
                  <label className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-100 cursor-pointer">
                    <span>
                      <span className="block font-medium text-gray-800">Marketing</span>
                      <span className="block text-[11px] text-gray-400">Offers, broadcasts, birthday wishes</span>
                    </span>
                    <input type="checkbox" checked={notifPrefs.marketing}
                      onChange={(e) => setNotifPrefs(prev => ({ ...prev, marketing: e.target.checked }))}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded" />
                  </label>
                </div>

                {/* OTP input (optional verification) */}
                {showOtpInput && !isWhatsappVerified && (
                  <div className="mt-3 p-3 bg-white border border-gray-200 rounded-lg space-y-2">
                    <p className="text-xs font-medium text-gray-600">Enter 6-digit OTP sent to WhatsApp</p>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        maxLength={6}
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none text-center tracking-widest font-bold"
                        placeholder="000000"
                      />
                      <button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={isVerifyingOtp || otp.length !== 6}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm active:scale-95 disabled:opacity-50 text-sm font-bold"
                      >
                        {isVerifyingOtp ? 'Processing…' : 'Verify'}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowOtpInput(false)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Edit Phone Number / Resend
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="sticky bottom-0 bg-white pt-4 pb-2 border-t border-gray-200 flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name.trim() || !formData.phoneNo.trim() || !formData.gymId}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 font-semibold"
            >
              {loading && (
                <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              <span>{loading ? 'Creating...' : 'Create Member'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
